import React, { useState } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { FaMinus, FaPlus } from "react-icons/fa";

const Container = styled.section`
  margin: 0 auto;
  padding: 4rem 1rem;
  font-family: "Manrope", sans-serif;
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #102a8e;
  text-align: center;
  margin-bottom: 1rem;
  font-weight: bold;
`;

const Subtitle = styled.p`
  text-align: center;
  color: #666;
  margin-bottom: 3rem;
  font-size: 14px;

  a {
    color: #5575f7;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const AccordionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 100%; // 1200px;
  margin: 0 auto;
`;

const AccordionItem = styled.div``;

interface QuestionButtonProps {
  isOpen: boolean;
}

const QuestionButton = styled.button<QuestionButtonProps>`
  width: 100%;
  text-align: left;
  padding: 1rem 2rem;
  background: #fafafa;
  border-radius: 10px;
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #102a8e;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  font-family: "Manrope", sans-serif;

  svg {
    width: 10px;
    height: 10px;
    color: #333333;
    transition: transform 0.3s ease;
  }
`;

const Answer = styled(motion.div)`
  padding: 1rem 0;
  color: #131313;
  font-size: 14px;
  line-height: 1.6;
  font-family: "Manrope", sans-serif;
  font-weight: 400;
`;

export const Faq: React.FC = () => {
  const [openQuestion, setOpenQuestion] = useState<number | null>(0);

  const questions = [
    {
      id: 1,
      question: "What does Compelling Candidature help me prepare for?",
      answer: "Compelling Candidature helps healthcare professionals prepare for UK applications, interviews, and workplace expectations through targeted courses and interview question banks.",
    },
    {
      id: 2,
      question: "Are there interview practice questions available?",
      answer:
        "Yes. We provide interview question banks and scenario-based prompts designed around real healthcare recruitment and interview processes.",
    },
    {
      id: 3,
      question: "What is included in the premium plan?",
      answer:
        "Premium members get access to all courses, unlimited quizzes, personalized study plans, and progress tracking features.",
    },
    {
      id: 4,
      question: "Can I cancel my subscription anytime?",
      answer:
        "Yes, you can cancel your subscription at any time from your account settings. Your access will continue until the end of your billing period.",
    },
    {
      id: 5,
      question: "What if I need help?",
      answer:
        "Our support team is available 24/7 to assist you with any questions or concerns you may have about the platform.",
    },
    {
      id: 6,
      question: "Who is Compelling Candidature designed for?",
      answer:
        "The platform is designed for healthcare professionals and students preparing for roles, progression, or transitions within UK healthcare settings.",
    },
    {
      id: 7,
      question: "Do I need to complete everything to benefit from the platform?",
      answer:
        "No. You can use specific courses or interview resources based on your individual needs and stage of preparation.",
    },
  ];

  const toggleQuestion = (id: number) => {
    setOpenQuestion(openQuestion === id ? null : id);
  };

  return (
    <Container>
      <Title>Frequently asked questions</Title>
      <Subtitle>
        These are common and frequently asked questions. Can't find what you're
        looking for? Reach out to us by{" "}
        <Link to="/contact">contacting us here</Link>
      </Subtitle>

      <AccordionContainer>
        {questions.map((q) => (
          <AccordionItem key={q.id}>
            <QuestionButton
              onClick={() => toggleQuestion(q.id)}
              isOpen={openQuestion === q.id}
            >
              {q.question}
              {openQuestion === q.id ? <FaMinus /> : <FaPlus />}
            </QuestionButton>
            <AnimatePresence>
              {openQuestion === q.id && (
                <Answer
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {q.answer}
                </Answer>
              )}
            </AnimatePresence>
          </AccordionItem>
        ))}
      </AccordionContainer>
    </Container>
  );
};
